import { observable, runInAction } from 'mobx';
import { postJSON, setAdminToken } from '../utils/Request';

export type AdminUser = {
  userUid: string;
  token: string;
  accessToken: string;
  userName: string;
  roleLabel: string;
};

const STORAGE_KEY = 'admin_user';

export default class AdminLoginStore {
  data = observable.object<{ hasLogin: boolean; userinfo?: AdminUser }>({
    hasLogin: false
  });

  constructor() {
    const user = this.readStoredUser();
    if (user) {
      runInAction(() => {
        this.data.hasLogin = true;
        this.data.userinfo = user;
      });
      setAdminToken(user.accessToken);
    }
  }

  private readStoredUser(): AdminUser | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw || raw === 'undefined' || raw === 'null') {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as Partial<AdminUser>;
      const accessToken = parsed.accessToken?.trim() || parsed.token?.trim() || '';
      if (!parsed || typeof parsed !== 'object' || !accessToken) return null;
      return {
        userUid: parsed.userUid ?? '',
        token: accessToken,
        accessToken,
        userName: parsed.userName ?? '',
        roleLabel: parsed.roleLabel || '运营管理员'
      };
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  async login(loginName: string, password: string) {
    const res = await postJSON({
      path: '/api/v1/auth/console/login',
      params: { loginName, password }
    });
    if (res.code !== 200) return res;
    const session = toAdminSession(res.content ?? res.data, loginName);
    if (!session) return res;
    runInAction(() => {
      this.data.hasLogin = true;
      this.data.userinfo = session;
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setAdminToken(session.accessToken);
    return res;
  }

  async logout() {
    try {
      await postJSON({ path: '/api/v1/auth/console/logout' });
    } catch {
      // 接口失败也要清掉本地会话，避免停在已登录页面。
    }
    localStorage.removeItem(STORAGE_KEY);
    setAdminToken(null);
    runInAction(() => {
      this.data.hasLogin = false;
      this.data.userinfo = undefined;
    });
  }
}

function textOf(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function toAdminSession(content: unknown, loginName: string): AdminUser | null {
  const root = asRecord(content);
  const accessToken = root ? textOf(root, ['accessToken']) : '';
  if (!accessToken) return null;
  return {
    userUid: loginName,
    token: accessToken,
    accessToken,
    userName: loginName,
    roleLabel: '运营管理员'
  };
}
